/**
 * Which columns a template's table shows, before and after a run.
 *
 *   const cols = useTemplateColumns(template, preset);
 *   cols.columns            what the preview and the results table show
 *   cols.setColumns(next)   the picker
 *
 * With a preset open, starts from the preset's columns (or the template's own if it saved none), and changes stay
 * with the page until "Update preset". Without one, starts from the last choice for this template in this browser, or
 * the template's own columns, and remembers changes. A choice holds each column's key, label and format, so added
 * fields show in the preview before anything runs.
 *
 * Pass the template as templateFor() shapes it for the chosen options: each option (a Period, a tab, contacts on)
 * has its own default columns and its own remembered choice (`variantKey`).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { TemplateColumn, TemplateDetail } from "@/services/templates.service";

const storageKey = (slug: string, variantKey?: string) => `spidra.template-columns.${slug}${variantKey ? `.${variantKey}` : ""}`;

function readSaved(template: TemplateDetail): TemplateColumn[] | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey(template.slug, template.variantKey)) || "null");
    if (!Array.isArray(saved) || !saved.length) return null;
    // Older choices were a list of keys; keys the template knows keep their labels, others are dropped.
    const byKey = new Map(template.columns.map(c => [c.key, c]));
    const cols = saved
      .map(c => (typeof c === "string" ? byKey.get(c) : c && typeof c.key === "string" && typeof c.label === "string" ? (c as TemplateColumn) : undefined))
      .filter((c): c is TemplateColumn => !!c);
    return cols.length ? cols : null;
  } catch {
    return null;
  }
}

export function useTemplateColumns(template: TemplateDetail | null, preset: { columns: TemplateColumn[] | null } | null) {
  const [columns, setState] = useState<TemplateColumn[] | null>(null);

  // Reloaded when the template or its options change, not on every keystroke (the template object is rebuilt then).
  const key = template ? storageKey(template.slug, template.variantKey) : null;
  const latest = useRef(template);
  latest.current = template;
  useEffect(() => {
    const t = latest.current;
    if (!t) return;
    setState(preset ? (preset.columns?.length ? preset.columns : null) : readSaved(t));
  }, [key, preset]);

  const setColumns = useCallback(
    (next: TemplateColumn[]) => {
      setState(next);
      // While a preset is open, changes belong to it, not to this template's default.
      if (!template || preset) return;
      try {
        window.localStorage.setItem(storageKey(template.slug, template.variantKey), JSON.stringify(next));
      } catch {
        /* the choice lasts for this visit */
      }
    },
    [template, preset]
  );

  return { columns: columns ?? template?.columns ?? [], setColumns, isCustom: !!columns };
}
